import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Container, Typography, Button, TextField, CircularProgress, Paper, Chip, Stack,
  ToggleButton, ToggleButtonGroup, IconButton, Slider, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import GestureIcon from '@mui/icons-material/Gesture';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CropIcon from '@mui/icons-material/Crop';
import { getSignDocument, getSignPages, submitSignature } from '../../api';
import CameraCapture from '../common/CameraCapture';
import ImageCropper from '../common/ImageCropper';

const ACCENT = '#0e7c86';
const ACCENT_DARK = '#0a5a62';
const SIG_COLORS = ['#15232b', '#1a3ea8', '#c0392b'];
const SIG_FONTS = [
  '"Brush Script MT", "Segoe Script", cursive',
  '"Segoe Script", "Comic Sans MS", cursive',
  '"Lucida Handwriting", "Brush Script MT", cursive',
];
const CHECKER = 'linear-gradient(45deg,#00000010 25%,transparent 25%),linear-gradient(-45deg,#00000010 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#00000010 75%),linear-gradient(-45deg,transparent 75%,#00000010 75%)';

function tokenFromPath() { const parts = window.location.pathname.split('/').filter(Boolean); return parts[parts.length - 1]; }
function todayStr() { const d = new Date(); const p = (n) => String(n).padStart(2, '0'); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`; }

function measureAspect(dataUrl, cb) { const im = new Image(); im.onload = () => cb(im.naturalWidth ? im.naturalHeight / im.naturalWidth : 0.35); im.src = dataUrl; }

// Render a date string to a tight transparent PNG.
const DATE_FONT = 'Calibri, "Segoe UI", Carlito, Arial, sans-serif';
function renderDateImage(text, color) {
  const meas = document.createElement('canvas').getContext('2d');
  meas.font = `44px ${DATE_FONT}`;
  const w = Math.max(60, Math.ceil(meas.measureText(text || ' ').width) + 20);
  const h = 60;
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const ctx = c.getContext('2d'); ctx.fillStyle = color; ctx.textBaseline = 'middle';
  ctx.font = `44px ${DATE_FONT}`;
  ctx.fillText(text || '', 10, h / 2 + 2);
  return { img: c.toDataURL('image/png'), aspect: h / w };
}

// Strip a photo's background: bright pixels -> transparent.
function removeBackground(dataUrl, threshold) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight;
      const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0);
      try {
        const d = ctx.getImageData(0, 0, c.width, c.height); const a = d.data;
        for (let i = 0; i < a.length; i += 4) {
          const lum = 0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2];
          if (lum >= threshold) a[i + 3] = 0;
          else a[i + 3] = Math.min(255, Math.round(((threshold - lum) / threshold) * 255 * 1.7));
        }
        ctx.putImageData(d, 0, 0); resolve(c.toDataURL('image/png'));
      } catch (e) { resolve(dataUrl); }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function ColorSwatches({ color, setColor }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: '#64757d' }}>Colour</Typography>
      {SIG_COLORS.map((c) => (
        <Box key={c} onClick={() => setColor(c)} sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer', boxShadow: color === c ? `0 0 0 2px #fff, 0 0 0 4px ${ACCENT}` : '0 0 0 1px #ccc' }} />
      ))}
    </Stack>
  );
}

function SignaturePad({ color, onChange }) {
  const canvasRef = useRef(null); const drawing = useRef(false); const last = useRef({ x: 0, y: 0 }); const dirty = useRef(false); const colorRef = useRef(color);
  useEffect(() => { colorRef.current = color; }, [color]);
  const setup = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect?.() || {};
    const width = rect.width || canvas.clientWidth || 300;
    const height = rect.height || canvas.clientHeight || 150;
    canvas.width = width * ratio; canvas.height = height * ratio;
    const ctx = canvas.getContext('2d'); ctx.scale(ratio, ratio); ctx.lineWidth = 2.6; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  }, []);
  useEffect(() => { setup(); const r = () => setup(); window.addEventListener('resize', r); return () => window.removeEventListener('resize', r); }, [setup]);
  const pos = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect?.() || { left: 0, top: 0 };
    const source = e.touches?.length ? e.touches[0] : e;
    return { x: source.clientX - (rect.left || 0), y: source.clientY - (rect.top || 0) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.strokeStyle = colorRef.current; ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); last.current = p; dirty.current = true; };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <Box>
      <Box sx={{ position: 'relative', border: '2px dashed #cfdbe0', borderRadius: 2, bgcolor: '#f8fafb', height: 150, overflow: 'hidden' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair', display: 'block' }} onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        <Typography sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', color: '#9aa7ad', fontSize: 12, pointerEvents: 'none' }}>Draw your signature here</Typography>
      </Box>
      <Button size="small" variant="outlined" onClick={clear} sx={{ mt: 1, textTransform: 'none', borderColor: '#d5e0e4', color: '#64757d' }}>Clear</Button>
    </Box>
  );
}

function SignatureType({ color, onChange }) {
  const [text, setText] = useState(''); const [font, setFont] = useState(0);
  useEffect(() => {
    if (!text.trim()) { onChange(null); return; }
    const c = document.createElement('canvas'); c.width = 640; c.height = 170;
    const ctx = c.getContext('2d'); ctx.fillStyle = color; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
    let size = 74; const apply = (s) => { ctx.font = `${s}px ${SIG_FONTS[font]}`; }; apply(size);
    while (ctx.measureText(text).width > 600 && size > 24) { size -= 3; apply(size); }
    ctx.fillText(text, 18, 90); onChange(c.toDataURL('image/png'));
  }, [text, font, color, onChange]);
  return (
    <Box>
      <TextField fullWidth size="small" placeholder="Type your full name" value={text} onChange={(e) => setText(e.target.value)} sx={{ mb: 1.5 }} />
      <Box sx={{ border: '2px dashed #cfdbe0', borderRadius: 2, bgcolor: '#f8fafb', minHeight: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, overflow: 'hidden' }}>
        {text ? <Typography sx={{ fontFamily: SIG_FONTS[font], fontSize: 42, color, lineHeight: 1, whiteSpace: 'nowrap' }}>{text}</Typography> : <Typography sx={{ color: '#9aa7ad', fontSize: 12.5 }}>Your typed signature preview appears here</Typography>}
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1.2 }}>
        {SIG_FONTS.map((f, i) => (
          <Button key={i} size="small" variant={font === i ? 'contained' : 'outlined'} onClick={() => setFont(i)} sx={{ textTransform: 'none', fontFamily: f, fontSize: 18, py: 0.2, px: 1.5, minWidth: 0, lineHeight: 1.4, ...(font === i ? { bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK } } : { borderColor: '#d5e0e4', color: '#333' }) }}>{text ? text.slice(0, 8) : 'Abc'}</Button>
        ))}
      </Stack>
    </Box>
  );
}

function SignatureUpload({ value, onChange }) {
  const [raw, setRaw] = useState(null); const [removeBg, setRemoveBg] = useState(true); const [strength, setStrength] = useState(205);
  const [cam, setCam] = useState(false); const [crop, setCrop] = useState(false); const [err, setErr] = useState(null);
  useEffect(() => { if (!raw) return; if (removeBg) removeBackground(raw, strength).then(onChange); else onChange(raw); }, [raw, removeBg, strength, onChange]);
  const onFile = (file) => { setErr(null); if (!file) return; if (!/^image\/(png|jpe?g)$/.test(file.type)) { setErr('Please choose a PNG or JPG image.'); return; } if (file.size > 5 * 1024 * 1024) { setErr('Image must be under 5 MB.'); return; } const r = new FileReader(); r.onload = () => { setRaw(r.result); setCrop(true); }; r.readAsDataURL(file); };
  const clear = () => { setRaw(null); onChange(null); };
  return (
    <Box>
      <Box sx={{ border: '2px dashed #cfdbe0', borderRadius: 2, backgroundImage: value ? CHECKER : 'none', backgroundSize: '14px 14px', backgroundColor: '#f8fafb', minHeight: 130, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2 }}>
        {value ? <img src={value} alt="signature" style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }} /> : <><UploadFileIcon sx={{ color: '#9aa7ad', fontSize: 30 }} /><Typography sx={{ color: '#9aa7ad', fontSize: 12.5, textAlign: 'center' }}>Upload or photograph your signature — the background is removed automatically</Typography></>}
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button component="label" size="small" variant="outlined" sx={{ textTransform: 'none', borderColor: '#d5e0e4', color: ACCENT_DARK }}>{value ? 'Different' : 'Choose image'}<input hidden type="file" accept="image/png,image/jpeg" onChange={(e) => onFile(e.target.files?.[0])} /></Button>
          <Button size="small" variant="outlined" startIcon={<CameraAltIcon />} onClick={() => setCam(true)} sx={{ textTransform: 'none', borderColor: '#d5e0e4', color: ACCENT_DARK }}>Camera</Button>
          {raw && <Button size="small" variant="outlined" startIcon={<CropIcon />} onClick={() => setCrop(true)} sx={{ textTransform: 'none', borderColor: '#d5e0e4', color: ACCENT_DARK }}>Crop</Button>}
          {value && <Button size="small" onClick={clear} sx={{ textTransform: 'none', color: '#64757d' }}>Clear</Button>}
        </Stack>
      </Box>
      <FormControlLabel sx={{ mt: 1 }} control={<Switch size="small" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} />} label={<Typography sx={{ fontSize: 13, color: '#46606c' }}>Auto-remove background</Typography>} />
      {removeBg && raw && (<Box sx={{ px: 0.5 }}><Typography sx={{ fontSize: 12, color: '#64757d' }}>Cleanup strength</Typography><Slider size="small" min={120} max={245} value={strength} onChange={(e, v) => setStrength(v)} sx={{ color: ACCENT }} /></Box>)}
      {err && <Typography sx={{ color: '#ce4b4b', fontSize: 12, mt: 0.5 }}>{err}</Typography>}
      <CameraCapture open={cam} title="Take a photo of your signature" onClose={() => setCam(false)} onCapture={(d) => { setRaw(d); setCrop(true); }} />
      <ImageCropper open={crop && !!raw} image={raw} onCancel={() => setCrop(false)} onCrop={(d) => { setRaw(d); setCrop(false); }} />
    </Box>
  );
}

// ---- Document with draggable overlays (signature + date) -----------------
function PlacementCanvas({ pages, page, setPage, overlays, selectedId, onSelect, onUpdate, onDelete }) {
  const wrapRef = useRef(null); const drag = useRef(null);
  const dims = () => { const el = wrapRef.current; return el ? { W: el.clientWidth, H: el.clientHeight } : { W: 1, H: 1 }; };
  const down = (id, mode, e, o) => { e.preventDefault(); e.stopPropagation(); onSelect(id); const p = e.touches ? e.touches[0] : e; drag.current = { id, mode, sx: p.clientX, sy: p.clientY, o: { ...o } }; };
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const p = e.touches ? e.touches[0] : e; const { W, H } = dims();
    const dx = p.clientX - drag.current.sx, dy = p.clientY - drag.current.sy; const o = drag.current.o;
    if (drag.current.mode === 'move') onUpdate(drag.current.id, { x: Math.max(0, Math.min(o.x + dx, W - o.w)), y: Math.max(0, Math.min(o.y + dy, H - o.h)) });
    else if (drag.current.mode === 'resize') { const w = Math.max(40, Math.min(o.w + dx, W - o.x)); onUpdate(drag.current.id, { w, h: w * o.aspect }); }
    else if (drag.current.mode === 'rotate') { const el = wrapRef.current.getBoundingClientRect(); const cx = el.left + o.x + o.w / 2, cy = el.top + o.y + o.h / 2; let ang = Math.atan2(p.clientY - cy, p.clientX - cx) * 180 / Math.PI + 90; while (ang > 180) ang -= 360; while (ang < -180) ang += 360; onUpdate(drag.current.id, { rot: Math.round(ang) }); }
  }, [onUpdate]);
  const onUp = useCallback(() => { drag.current = null; }, []);
  useEffect(() => { window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp); window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onUp); return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); }; }, [onMove, onUp]);

  const shown = overlays.filter((o) => o.page === page);
  return (
    <Box>
      {pages.length > 1 && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
          <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeftIcon /></IconButton>
          <Typography sx={{ fontSize: 12.5, color: '#64757d' }}>Page {page + 1} of {pages.length}</Typography>
          <IconButton size="small" disabled={page === pages.length - 1} onClick={() => setPage(page + 1)}><ChevronRightIcon /></IconButton>
        </Stack>
      )}
      <Box id="sig-page-box" ref={wrapRef} onMouseDown={() => onSelect(null)} onTouchStart={() => onSelect(null)}
        sx={{ position: 'relative', width: '100%', maxWidth: 760, mx: 'auto', border: '1px solid #e2e9ec', borderRadius: 1, overflow: 'hidden', boxShadow: '0 2px 14px rgba(16,40,50,.08)', lineHeight: 0, touchAction: 'none' }}>
        <img src={pages[page].image} alt={`page ${page + 1}`} style={{ width: '100%', display: 'block', userSelect: 'none' }} draggable={false} />
        {shown.map((o) => (
          <Box key={o.id} onMouseDown={(e) => down(o.id, 'move', e, o)} onTouchStart={(e) => down(o.id, 'move', e, o)}
            sx={{ position: 'absolute', left: o.x, top: o.y, width: o.w, height: o.h, opacity: o.opacity, transform: `rotate(${o.rot}deg)`, transformOrigin: 'center center', cursor: 'grab', border: selectedId === o.id ? `1.5px dashed ${ACCENT}` : '1.5px dashed transparent', background: selectedId === o.id ? 'rgba(14,124,134,0.05)' : 'transparent', boxSizing: 'border-box' }}>
            <img src={o.img} alt={o.kind} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
            {selectedId === o.id && (
              <>
                <Box onMouseDown={(e) => down(o.id, 'resize', e, o)} onTouchStart={(e) => down(o.id, 'resize', e, o)} sx={{ position: 'absolute', right: -8, bottom: -8, width: 16, height: 16, borderRadius: '50%', bgcolor: ACCENT, border: '2px solid #fff', cursor: 'nwse-resize', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                <Box onMouseDown={(e) => down(o.id, 'rotate', e, o)} onTouchStart={(e) => down(o.id, 'rotate', e, o)} sx={{ position: 'absolute', left: '50%', top: -28, ml: '-9px', width: 18, height: 18, borderRadius: '50%', bgcolor: '#fff', border: `2px solid ${ACCENT}`, color: ACCENT, display: 'grid', placeItems: 'center', cursor: 'grab', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }}><RotateRightIcon sx={{ fontSize: 12 }} /></Box>
                <Box onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(o.id); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(o.id); }} title="Delete" sx={{ position: 'absolute', right: -9, top: -9, width: 18, height: 18, borderRadius: '50%', bgcolor: '#c0392b', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }}><CloseIcon sx={{ fontSize: 12 }} /></Box>
              </>
            )}
          </Box>
        ))}
      </Box>
      <Typography sx={{ color: '#8a9099', fontSize: 11.5, mt: 1, textAlign: 'center' }}>Drag to move · corner dot to resize · top dot to rotate · click the page to deselect</Typography>
    </Box>
  );
}

const StatusChip = ({ status }) => {
  const map = { SENT: ['Awaiting your signature', '#b3801a', '#f6eeda'], VIEWED: ['Awaiting your signature', '#b3801a', '#f6eeda'], RETURNED: ['Signed & returned', '#2e9e6b', '#e2f3ea'], ACKNOWLEDGED: ['Acknowledged', '#2e9e6b', '#e2f3ea'] };
  const [label, color, bg] = map[status] || [status, '#5c7385', '#e9eef1'];
  return <Chip label={label} size="small" sx={{ color, bgcolor: bg, fontWeight: 600 }} />;
};

export default function OnboardingSign() {
  const token = tokenFromPath();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [done, setDone] = useState(false);

  const [mode, setMode] = useState('draw');
  const [color, setColor] = useState(SIG_COLORS[0]);
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [dateText, setDateText] = useState(todayStr());

  const [pages, setPages] = useState(null);
  const [page, setPage] = useState(0);
  const [overlays, setOverlays] = useState([]);   // {id,kind,img,page,x,y,w,h,rot,opacity,aspect}
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pageRef = useRef(0);
  useEffect(() => { pageRef.current = page; }, [page]);

  const updateOverlay = useCallback((id, patch) => { setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o))); }, []);
  const deleteOverlay = useCallback((id) => {
    if (id === 'sig') setSignature(null);   // the sync effect then drops the overlay
    else setOverlays((prev) => prev.filter((o) => o.id !== id));
    setSelectedId((s) => (s === id ? null : s));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getSignDocument(token);
        if (!alive) return;
        setData(res); setSignerName(res?.employee?.name || ''); setDone(!!res?.already_signed);
        if (!res?.already_signed) {
          try { const pg = await getSignPages(token); if (!alive) return; const list = pg.pages || []; setPages(list); setPage(Math.max(0, list.length - 1)); }
          catch { if (alive) setPages([]); }
        }
      } catch (e) { if (alive) setError(e?.response?.data?.error || 'This signing link is invalid or has expired.'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  // Keep a 'sig' overlay in sync with the created signature.
  useEffect(() => {
    if (!signature) { setOverlays((prev) => prev.filter((o) => o.kind !== 'sig')); return; }
    measureAspect(signature, (asp) => {
      setOverlays((prev) => {
        const ex = prev.find((o) => o.kind === 'sig');
        if (ex) return prev.map((o) => (o.kind === 'sig' ? { ...o, img: signature, aspect: asp, h: o.w * asp } : o));
        const wrap = document.getElementById('sig-page-box');
        const W = wrap ? wrap.clientWidth : 600, H = wrap ? wrap.clientHeight : 800; const w = 180;
        return [...prev, { id: 'sig', kind: 'sig', img: signature, page: pageRef.current, x: W * 0.1, y: H * 0.72, w, h: w * asp, rot: 0, opacity: 1, aspect: asp }];
      });
      setSelectedId('sig');
    });
  }, [signature]);

  // Keep the date overlay's image current with the text/colour.
  useEffect(() => {
    setOverlays((prev) => {
      if (!prev.find((o) => o.kind === 'date')) return prev;
      const { img, aspect } = renderDateImage(dateText, color);
      return prev.map((o) => (o.kind === 'date' ? { ...o, img, aspect, h: o.w * aspect } : o));
    });
  }, [dateText, color]);

  const addDate = () => {
    const { img, aspect } = renderDateImage(dateText, color);
    setOverlays((prev) => {
      if (prev.find((o) => o.kind === 'date')) return prev.map((o) => (o.kind === 'date' ? { ...o, img, aspect, h: o.w * aspect } : o));
      const wrap = document.getElementById('sig-page-box');
      const W = wrap ? wrap.clientWidth : 600, H = wrap ? wrap.clientHeight : 800; const w = 110;
      return [...prev, { id: 'date', kind: 'date', img, page: pageRef.current, x: W * 0.5, y: H * 0.72, w, h: w * aspect, rot: 0, opacity: 1, aspect }];
    });
    setSelectedId('date');
  };
  const removeDate = () => { setOverlays((prev) => prev.filter((o) => o.kind !== 'date')); setSelectedId((s) => (s === 'date' ? null : s)); };
  const hasDate = overlays.some((o) => o.kind === 'date');

  const handleSubmit = () => {
    if (!signature) { setError('Please add your signature first.'); return; }
    if (!signerName.trim()) { setError('Please enter your full name as signed.'); return; }
    setError(null);
    setConfirmOpen(true);
  };

  const confirmSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true); setError(null);
    let placements = null;
    const wrap = document.getElementById('sig-page-box');
    if (pages && pages.length && wrap) {
      const W = wrap.clientWidth, H = wrap.clientHeight;
      placements = overlays.map((o) => {
        const base = { kind: o.kind === 'date' ? 'date' : 'signature', page: o.page, x: o.x / W, y: o.y / H, w: o.w / W, rotation: o.rot, opacity: o.opacity };
        if (o.kind === 'date') base.image = o.img;   // date carries its own image; sig uses the main signature
        return base;
      });
    }
    try {
      const res = await submitSignature(token, { signature, signer_name: signerName, placements });
      setData((d) => ({ ...d, document: res.document })); setDone(true);
    } catch (e) { setError(e?.response?.data?.error || 'Could not submit your signature. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const shell = (children, wide) => (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef2f4', py: { xs: 2, md: 3.5 } }}>
      <Container maxWidth={wide ? false : 'sm'} sx={wide ? { px: { xs: 1.5, md: 3 } } : {}}>
        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 1.2, background: `linear-gradient(150deg, ${ACCENT}, ${ACCENT_DARK})`, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14 }}>ATS</Box>
          <Box><Typography sx={{ fontWeight: 700, fontSize: 15, color: '#14232b', lineHeight: 1.1 }}>ATS Share Brokers Pvt. Ltd.</Typography><Typography sx={{ fontSize: 11, color: '#7b96a2', letterSpacing: '.5px', textTransform: 'uppercase' }}>Document Signing</Typography></Box>
        </Stack>
        {children}
      </Container>
    </Box>
  );

  if (loading) return shell(<Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}><CircularProgress sx={{ color: ACCENT }} /></Paper>);
  if (error && !data) return shell(<Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}><Typography sx={{ fontWeight: 700, fontSize: 18, color: '#14232b', mb: 1 }}>Link unavailable</Typography><Typography sx={{ color: '#64757d' }}>{error}</Typography></Paper>);

  const doc = data?.document || {}; const emp = data?.employee || {};
  if (done) return shell(
    <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: 'center', boxShadow: '0 4px 24px rgba(16,40,50,.08)', maxWidth: 520, mx: 'auto' }}>
      <CheckCircleIcon sx={{ fontSize: 54, color: '#2e9e6b', mb: 1 }} />
      <Typography sx={{ fontWeight: 700, fontSize: 19, color: '#14232b' }}>{doc.status === 'ACKNOWLEDGED' ? 'Acknowledged' : 'Signed & returned'}</Typography>
      <Typography sx={{ color: '#64757d', mt: 0.5 }}>A signed copy has been sent to HR and filed against your employee record. You can close this page.</Typography>
      {doc.signed_url && <Button href={doc.signed_url} target="_blank" rel="noreferrer" sx={{ mt: 2, color: ACCENT_DARK, textTransform: 'none' }}>Download signed copy</Button>}
    </Paper>
  );

  const hasEditor = pages && pages.length > 0;
  const sel = overlays.find((o) => o.id === selectedId);

  const sidebar = (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#e1f1f2', color: ACCENT_DARK, display: 'grid', placeItems: 'center', flex: 'none' }}><DescriptionIcon /></Box>
        <Box sx={{ minWidth: 0, flex: 1 }}><Typography sx={{ fontWeight: 700, fontSize: 17, color: '#14232b', lineHeight: 1.2 }}>{doc.title}</Typography><Typography sx={{ color: '#64757d', fontSize: 12.5 }}>{emp.name} · {emp.employee_no}</Typography></Box>
        <StatusChip status={doc.status} />
      </Stack>

      <Typography sx={{ fontWeight: 700, color: '#14232b', mb: 1 }}>1. Add your signature</Typography>
      <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, v) => { if (v) { setMode(v); setSignature(null); } }} sx={{ mb: 1.5 }}>
        <ToggleButton value="draw" sx={{ textTransform: 'none', px: 1.7 }}><GestureIcon sx={{ fontSize: 18, mr: 0.6 }} /> Draw</ToggleButton>
        <ToggleButton value="type" sx={{ textTransform: 'none', px: 1.7 }}><TextFieldsIcon sx={{ fontSize: 18, mr: 0.6 }} /> Type</ToggleButton>
        <ToggleButton value="upload" sx={{ textTransform: 'none', px: 1.7 }}><UploadFileIcon sx={{ fontSize: 18, mr: 0.6 }} /> Upload</ToggleButton>
      </ToggleButtonGroup>
      {mode === 'draw' && <SignaturePad color={color} onChange={setSignature} />}
      {mode === 'type' && <SignatureType color={color} onChange={setSignature} />}
      {mode === 'upload' && <SignatureUpload value={signature} onChange={setSignature} />}
      {(mode === 'draw' || mode === 'type') && <ColorSwatches color={color} setColor={setColor} />}

      <TextField fullWidth size="small" label="Full name (as signed)" value={signerName} onChange={(e) => setSignerName(e.target.value)} sx={{ mt: 2 }} />

      {hasEditor && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: '#14232b', mb: 1 }}>2. Add a date <Box component="span" sx={{ fontWeight: 400, color: '#9aa7ad', fontSize: 13 }}>(optional)</Box></Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="Date" value={dateText} onChange={(e) => setDateText(e.target.value)} sx={{ flex: 1 }} />
            {hasDate
              ? <Button variant="outlined" onClick={removeDate} sx={{ textTransform: 'none', borderColor: '#e0b4b4', color: '#c0392b' }}>Remove</Button>
              : <Button variant="outlined" startIcon={<CalendarMonthIcon />} onClick={addDate} sx={{ textTransform: 'none', borderColor: '#cfe3e5', color: ACCENT_DARK, whiteSpace: 'nowrap' }}>Add date</Button>}
          </Stack>
          {hasDate && <Typography sx={{ fontSize: 11.5, color: '#8a9099', mt: 0.5 }}>Date placed — drag it onto the document.</Typography>}
        </Box>
      )}

      {hasEditor && sel && (
        <Box sx={{ mt: 2.5, p: 1.5, border: '1px solid #e2e9ec', borderRadius: 2, bgcolor: '#fafcfc' }}>
          <Typography sx={{ fontWeight: 700, color: '#14232b', mb: 1, fontSize: 13.5 }}>Adjust the {sel.kind === 'date' ? 'date' : 'signature'}</Typography>
          <Typography sx={{ fontSize: 12, color: '#64757d' }}>Opacity — {Math.round(sel.opacity * 100)}%</Typography>
          <Slider size="small" min={20} max={100} value={Math.round(sel.opacity * 100)} onChange={(e, v) => updateOverlay(sel.id, { opacity: v / 100 })} sx={{ color: ACCENT }} />
          <Typography sx={{ fontSize: 12, color: '#64757d' }}>Size — {Math.round(sel.w)} px</Typography>
          <Slider size="small" min={50} max={360} value={Math.round(sel.w)} onChange={(e, v) => updateOverlay(sel.id, { w: v, h: v * sel.aspect })} sx={{ color: ACCENT }} />
          <Typography sx={{ fontSize: 12, color: '#64757d' }}>Rotation — {sel.rot}°</Typography>
          <Slider size="small" min={-180} max={180} value={sel.rot} onChange={(e, v) => updateOverlay(sel.id, { rot: v })} sx={{ color: ACCENT }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button size="small" onClick={() => updateOverlay(sel.id, { rot: 0, opacity: 1 })} sx={{ textTransform: 'none', color: '#64757d' }}>Reset adjust</Button>
            <Button size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} onClick={() => deleteOverlay(sel.id)} sx={{ textTransform: 'none', color: '#c0392b' }}>Remove {sel.kind === 'date' ? 'date' : 'signature'}</Button>
          </Stack>
        </Box>
      )}

      {error && <Typography sx={{ color: '#ce4b4b', fontSize: 13, mt: 1.5 }}>{error}</Typography>}
      <Typography sx={{ color: '#8a9099', fontSize: 11.5, mt: 1.5 }}>By signing you confirm you have read and agree to this document. Your signature, name, date and IP are recorded for audit.</Typography>
      <Button fullWidth variant="contained" onClick={handleSubmit} disabled={submitting || !signature} sx={{ mt: 1.5, py: 1.2, bgcolor: ACCENT, textTransform: 'none', fontWeight: 700, fontSize: 15, '&:hover': { bgcolor: ACCENT_DARK } }}>
        {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : `${data?.action_label || 'Sign & return'} to HR`}
      </Button>
      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm your signature</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64757d', fontSize: 13, mb: 1.5 }}>
            Review the signature and audit details before finalizing this document.
          </Typography>
          <Box sx={{ border: '1px solid #e2e9ec', borderRadius: 2, p: 1.5, bgcolor: '#fafcfc', textAlign: 'center' }}>
            {signature && <img src={signature} alt="Signature confirmation preview" style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }} />}
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={0.7}>
            <Typography sx={{ fontSize: 13 }}><strong>Document:</strong> {data?.document?.title}</Typography>
            <Typography sx={{ fontSize: 13 }}><strong>Signer:</strong> {signerName.trim()}</Typography>
            <Typography sx={{ fontSize: 13 }}><strong>Time:</strong> {new Date().toLocaleString()}</Typography>
            <Typography sx={{ fontSize: 13 }}><strong>IP:</strong> {data?.signing_metadata?.client_ip || 'Recorded securely on submission'}</Typography>
          </Stack>
          <Typography sx={{ color: '#8a9099', fontSize: 11.5, mt: 1.5 }}>
            The server records the exact UTC and IST timestamps and locks the finalized PDF against further application changes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting} sx={{ textTransform: 'none', color: '#64757d' }}>Go back</Button>
          <Button onClick={confirmSubmit} variant="contained" disabled={submitting} sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK } }}>
            Confirm and sign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (!hasEditor) return shell(
    <Paper sx={{ p: { xs: 2, md: 3.5 }, borderRadius: 3, boxShadow: '0 4px 24px rgba(16,40,50,.08)' }}>
      {sidebar}
      {doc.source_url && <Button fullWidth variant="outlined" startIcon={<DescriptionIcon />} href={doc.source_url} target="_blank" rel="noreferrer" sx={{ mt: 2, borderColor: '#d5e0e4', color: ACCENT_DARK, textTransform: 'none', fontWeight: 600 }}>Review the document (PDF)</Button>}
    </Paper>
  );

  return shell(
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 24px rgba(16,40,50,.08)' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: { xs: 2, md: 3 }, alignItems: 'start' }}>
        <Box>{sidebar}</Box>
        <Box><PlacementCanvas pages={pages} page={page} setPage={setPage} overlays={overlays} selectedId={selectedId} onSelect={setSelectedId} onUpdate={updateOverlay} onDelete={deleteOverlay} /></Box>
      </Box>
    </Paper>,
    true
  );
}
